/**
 * ============================================================================
 * gameApiService.ts - Server API Service for Game Actions
 * ============================================================================
 * 
 * This service handles all game-related API calls to the server.
 * All player actions, table operations, and game state changes go through
 * the server to ensure atomic operations and prevent client-side cheating.
 * 
 * ============================================================================
 */

import { auth } from '../firebase';

// ============================================================================
// CONFIGURATION
// ============================================================================

import { API_BASE } from '../utils/apiBase';
import geoService from './geoService';

// ============================================================================
// TYPES
// ============================================================================

export type PlayerAction = 'fold' | 'check' | 'call' | 'raise' | 'all-in';

export interface ActionResult {
  success: boolean;
  error?: string;
  gameState?: any;
  winner?: any;
  foldWin?: boolean;
  handComplete?: boolean;
  newRound?: string;
  nextPlayerId?: string;
  pot?: number;
}

export interface JoinTableResult {
  success: boolean;
  error?: string;
  chips?: number;
  seatIndex?: number;
  playerCount?: number;
  /** Server says the seat already exists - the buy-in was taken exactly once. */
  alreadySeated?: boolean;
}

export interface LeaveTableResult {
  success: boolean;
  error?: string;
  cashOut?: number;
  finalChips?: number;
}

export interface RebuyResult {
  success: boolean;
  error?: string;
  newChips?: number;
  totalChips?: number;
}

export interface TableInfo {
  id: string;
  name: string;
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  currency: 'playcoins' | 'pkcash';
  playerCount: number;
  maxPlayers: number;
  status: string;
}

// ============================================================================
// API UTILITIES
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

/**
 * An API failure that carries whether it is worth trying again. A dropped
 * connection or a 503 says nothing about what the server did; a 400 is a
 * final answer.
 */
export class ApiError extends Error {
  status: number;
  transient: boolean;
  constructor(message: string, status: number, transient: boolean) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.transient = transient;
  }
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

async function apiCall<T>(endpoint: string, options: {
  method?: 'GET' | 'POST';
  body?: object;
  params?: Record<string, string>;
  /**
   * Number of extra attempts on a TRANSIENT failure. Leave unset for anything
   * that is not safe to run twice - a retry is a second request, not a second
   * read of the first one, so only endpoints the server makes idempotent may
   * set this.
   */
  retry?: number;
  timeoutMs?: number;
}): Promise<T> {
  let url = `${API_BASE}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams(options.params);
    url += `?${searchParams.toString()}`;
  }

  const attempts = 1 + (options.retry || 0);
  // The api function is a single large bundle, so a cold start is genuinely
  // slow. Without a ceiling a hung socket leaves the caller waiting forever.
  const timeoutMs = options.timeoutMs ?? 45000;
  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(600 * attempt);

    const token = await getAuthToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetch(url, {
        method: options.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });
    } catch (err: any) {
      // fetch only rejects when the request never completed: offline, DNS,
      // a blocked request, or our own abort. The raw browser text ("Failed
      // to fetch") means nothing to a player, so it does not reach them.
      clearTimeout(timer);
      lastError = new ApiError(
        err?.name === 'AbortError'
          ? 'The server took too long to respond. Please try again.'
          : 'Could not reach the server. Check your connection and try again.',
        0, true);
      continue;
    }
    clearTimeout(timer);

    // Read as text first: an error from the load balancer (502/503) is HTML,
    // and response.json() on it throws a parse error that buries the status.
    const raw = await response.text().catch(() => '');
    let data: any = null;
    if (raw) { try { data = JSON.parse(raw); } catch { data = null; } }

    if (response.ok) return (data ?? {}) as T;

    // 5xx and 429 may succeed on another attempt; 4xx will not.
    const transient = response.status >= 500 || response.status === 429;
    lastError = new ApiError(
      data?.error || (transient
        ? 'The server is busy. Please try again in a moment.'
        : `Request failed (${response.status})`),
      response.status, transient);

    if (!transient) throw lastError;
  }

  throw lastError || new ApiError('Request failed', 0, true);
}

// ============================================================================
// GAME API SERVICE CLASS
// ============================================================================


// ── Creator program types ──────────────────────────────────────────────────
export interface HostQuota { usedThisMonth: number; freePerMonth: number; nextNeedsApproval: boolean }
export interface HostPermissions {
  canHost: boolean; isAdmin: boolean; mediaAllowed: 'none' | 'voice' | 'video'; mediaComped: boolean;
  buyIns?: Record<string, number[]>; structures?: string[];
}
export interface CreatorGroupInfo { id: string; name: string; memberCount: number; isPublic: boolean; joinCode: string | null }
export type GroupRole = 'owner' | 'moderator' | 'member' | null;
export interface GroupMemberCard { uid: string; name: string; photoURL: string | null; role: GroupRole }
export interface GroupChatMessage { id: string; senderId: string; senderName: string; senderPhoto: string | null; text: string; mentions?: string[]; system?: boolean; createdAt: number; deleted: boolean }
export interface HostTournamentRow {
  id: string; name: string; status: string; hostApproval: string | null; hostRejectNote: string | null;
  hostId: string; hostName: string | null; currency: string; buyInAmount: number; structure: string;
  startTime: string | null; createdAt: string | null; maxPlayers: number; registered: number;
  entry: 'code' | 'group' | 'open'; inviteCode: string | null; media: 'off' | 'voice' | 'video'; mediaScope: 'all' | 'final'; mediaComped: boolean;
  winnerId: string | null;
}
export interface HostCreatePayload {
  name: string; currency: 'playcoins' | 'pkcash'; buyIn: number; structure: string; startTime: string; maxPlayers: number;
  entry: 'code' | 'group' | 'open'; media: 'off' | 'voice' | 'video'; mediaScope: 'all' | 'final'; mediaComped?: boolean; groupId?: string;
}

class GameApiService {
  
  // ==========================================================================
  // PLAYER ACTIONS - All actions go through server
  // ==========================================================================

  /**
   * Send a player action to the server
   * This is the main method for all game actions (fold, check, call, raise, all-in)
   *  AUDIT FIX: Now sends handId and stateVersion to prevent race conditions

   */
  async playerAction(
    tableId: string,
    action: PlayerAction,
    amount: number = 0,
    handId?: string,
    stateVersion?: number,
    isTimeout: boolean = false
  ): Promise<ActionResult> {
    console.log(`🎯 [GameAPI] Sending action: ${action}${amount > 0 ? ` ($${amount})` : ''}${isTimeout ? ' (TIMEOUT)' : ''} to table ${tableId} (v${stateVersion || '?'})`);

    try {
      const result = await apiCall<ActionResult>('/playerAction', {
        method: 'POST',
        body: { 
          tableId, 
          action, 
          amount,
          //  AUDIT FIX: Send version info to reject stale actions

          handId,
          stateVersion,
          // ⏰ AWAY MODE: Track if this was an auto-timeout action
          isTimeout,
        },
      });

      console.log(`✅ [GameAPI] Action processed:`, {
        success: result.success,
        newRound: result.newRound,
        handComplete: result.handComplete,
        foldWin: result.foldWin,
      });

      return result;
    } catch (error) {
      console.error(`❌ [GameAPI] Action failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Action failed',
      };
    }
  }

  /**
   * Fold
   */
  async fold(tableId: string, handId?: string, stateVersion?: number): Promise<ActionResult> {
    return this.playerAction(tableId, 'fold', 0, handId, stateVersion);
  }

  /**
   * ⏰ Timeout Fold - Used when player's timer expires (tracks for away mode)
   */
  async timeoutFold(tableId: string, handId?: string, stateVersion?: number): Promise<ActionResult> {
    return this.playerAction(tableId, 'fold', 0, handId, stateVersion, true);
  }

  /**
   *  Kick Stalled Turn - any seated player/spectator can ping this when the

   * turn timer expires on someone ELSE'S turn (typically a bot whose
   * processor crashed). Server validates the deadline independently and
   * either re-fires the bot processor or force-folds, so the table moves
   * without waiting on the 1-minute enforceTurnTimeouts cron.
   */
  async kickStalledTurn(tableId: string): Promise<{ ok: boolean; action?: string; reason?: string }> {
    try {
      return await apiCall<{ ok: boolean; action?: string; reason?: string }>('/kickStalledTurn', {
        method: 'POST',
        body: { tableId },
      });
    } catch (err: any) {
      return { ok: false, reason: err?.message || 'request_failed' };
    }
  }

  /**
   * Tournament next-hand watchdog. Called when a tournament table has sat
   * at a hand boundary (no new deal) for too long - the server re-enters
   * its scheduler, which is claim-protected so concurrent kicks are safe.
   * Covers dead schedule chains AND the local emulator, where the rescue
   * crons never run.
   */
  /** Clear sitting-out (away) status on my seat - resumes normal play. */
  async imBack(tableId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<{ success: boolean; error?: string }>('/imBack', {
        method: 'POST',
        body: { tableId },
      });
    } catch (err: any) {
      return { success: false, error: err?.message || 'request_failed' };
    }
  }

  async kickTournamentDeal(tableId: string): Promise<{ ok: boolean; action?: string; reason?: string }> {
    try {
      return await apiCall<{ ok: boolean; action?: string; reason?: string }>('/kickTournamentDeal', {
        method: 'POST',
        body: { tableId },
      });
    } catch (err: any) {
      return { ok: false, reason: err?.message || 'request_failed' };
    }
  }

  /**
   * Check
   */
  async check(tableId: string, handId?: string, stateVersion?: number): Promise<ActionResult> {
    return this.playerAction(tableId, 'check', 0, handId, stateVersion);
  }

  /**
   * Call
   */
  async call(tableId: string, handId?: string, stateVersion?: number): Promise<ActionResult> {
    return this.playerAction(tableId, 'call', 0, handId, stateVersion);
  }

  /**
   * Raise
   */
  async raise(tableId: string, amount: number, handId?: string, stateVersion?: number): Promise<ActionResult> {
    return this.playerAction(tableId, 'raise', amount, handId, stateVersion);
  }

  /**
   * All-in
   */
  async allIn(tableId: string, handId?: string, stateVersion?: number): Promise<ActionResult> {
    return this.playerAction(tableId, 'all-in', 0, handId, stateVersion);
  }

  // ==========================================================================
  // TABLE OPERATIONS
  // ==========================================================================

  /**
   * Join a table (secure, server-validated)
   *  SECURITY: Pass stateCode for geo restriction check on PKCash tables

   */
  async secureJoinTable(
    tableId: string,
    buyInAmount: number,
    seatIndex?: number,
    stateCode?: string
  ): Promise<JoinTableResult> {
    console.log(`🪑 [GameAPI] Joining table ${tableId} with ${buyInAmount} chips`);

    try {
      // Retrying a join cannot double-charge: the server transaction rejects
      // a second join with "Already at table" before it touches the wallet.
      // That guard is what makes it safe to survive a dropped reply here,
      // which matters more on this call than almost any other - a lost
      // response used to leave the player staring at a browser error with no
      // idea whether their money had moved.
      const result = await apiCall<JoinTableResult>('/secureJoinTable', {
        method: 'POST',
        body: { tableId, buyInAmount, seatIndex, stateCode },
        retry: 2,
      });

      console.log(`✅ [GameAPI] Joined table:`, result);
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to join table';

      // "Already at table" after a retry means the FIRST attempt committed
      // and only its reply was lost. The seat exists and the buy-in was
      // taken once - reporting failure would be a lie, and would tempt the
      // player into buying in a second time.
      if (/already at table/i.test(message)) {
        console.warn('[GameAPI] Join reported already-seated - treating as seated');
        return { success: true, alreadySeated: true } as JoinTableResult;
      }

      console.error(`❌ [GameAPI] Join failed:`, error);
      return { success: false, error: message };
    }
  }
  
  // Alias for backwards compatibility
  async joinTable(tableId: string, buyInAmount: number, seatIndex?: number, stateCode?: string): Promise<JoinTableResult> {
    return this.secureJoinTable(tableId, buyInAmount, seatIndex, stateCode);
  }

  /**
   * Leave a table (secure, returns chips to wallet atomically)
   */
  async secureLeaveTable(tableId: string): Promise<LeaveTableResult> {
    console.log(`🚪 [GameAPI] Leaving table ${tableId}`);

    try {
      const result = await apiCall<LeaveTableResult>('/secureLeaveTable', {
        method: 'POST',
        body: { tableId },
      });

      console.log(`✅ [GameAPI] Left table:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [GameAPI] Leave failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to leave table',
      };
    }
  }
  
  // Alias for backwards compatibility
  async leaveTable(tableId: string): Promise<LeaveTableResult> {
    return this.secureLeaveTable(tableId);
  }

  /**
   * Rebuy at table (secure, deducts from wallet atomically)
   *  SECURITY: Pass stateCode for geo restriction check on PKCash tables

   */
  async secureRebuy(tableId: string, amount: number, stateCode?: string): Promise<RebuyResult> {
    console.log(`💰 [GameAPI] Rebuy at table ${tableId}: ${amount}`);

    try {
      const result = await apiCall<RebuyResult>('/secureRebuy', {
        method: 'POST',
        body: { tableId, amount, stateCode },
      });

      console.log(`✅ [GameAPI] Rebuy complete:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [GameAPI] Rebuy failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to rebuy',
      };
    }
  }
  
  // Alias for backwards compatibility
  async rebuy(tableId: string, amount: number): Promise<RebuyResult> {
    return this.secureRebuy(tableId, amount);
  }

  // ==========================================================================
  //  AUDIT FIX: TABLE RECOVERY

  // ==========================================================================

  /**
   * Force reset a stuck/corrupted table
   * @param mode - 'soft' (refund bets, restart) or 'hard' (full rebuild)
   */
  async forceResetTable(tableId: string, mode: 'soft' | 'hard' = 'soft'): Promise<{
    success: boolean;
    error?: string;
    mode?: string;
    tableEpoch?: number;
    totalRefunded?: number;
    playersRemaining?: number;
  }> {
    console.log(`🔧 [GameAPI] Force reset table ${tableId} (mode: ${mode})`);

    try {
      const result = await apiCall<any>('/forceResetTable', {
        method: 'POST',
        body: { tableId, mode },
      });

      console.log(`✅ [GameAPI] Force reset complete:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [GameAPI] Force reset failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reset table',
      };
    }
  }

  // ==========================================================================
  //  SECURE CARD FETCHING

  // ==========================================================================

  /**
   * Get own hole cards securely (server validates identity)
   * Cards are hidden in Firestore to prevent cheating via DevTools
   */
  async getMyCards(tableId: string): Promise<{
    success: boolean;
    holeCards?: { rank: string; suit: string }[];
    chips?: number;
    error?: string;
  }> {
    try {
      return await apiCall<{
        success: boolean;
        holeCards?: { rank: string; suit: string }[];
        chips?: number;
      }>('/getMyCards', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get cards',
      };
    }
  }

  /**
   * Show cards after winning by fold (voluntary reveal)
   * Server reads real cards from secure storage and updates gameState
   */
  async showCards(tableId: string): Promise<{
    success: boolean;
    cards?: { rank: string; suit: string }[];
    error?: string;
  }> {
    try {
      return await apiCall<{
        success: boolean;
        cards?: { rank: string; suit: string }[];
      }>('/showCards', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to show cards',
      };
    }
  }

  /**
   * Sit out of current hand
   */
  // Foreground/background reporter - powers background-only turn alerts.
  // The 'going background' report races iOS suspension. Webview fetches
  // can be frozen mid-flight, so on the native app the report ALSO goes
  // out via CapacitorHttp: that runs on iOS's own URLSession, which
  // finishes the request even after the webview's JS is suspended. Both
  // fire concurrently - double delivery is harmless (same idempotent
  // write), lost delivery auto-folds a player.
  async reportAppState(active: boolean): Promise<void> {
    try {
      if (!active) {
        const { auth } = await import('../firebase');
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        const { API_BASE } = await import('../utils/apiBase');
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        try {
          const { Capacitor, CapacitorHttp } = await import('@capacitor/core');
          if (Capacitor.isNativePlatform()) {
            CapacitorHttp.post({
              url: `${API_BASE}/appState`,
              headers,
              data: { active: false },
            }).catch(() => {});
          }
        } catch { /* native bridge unavailable - keepalive fetch below still fires */ }
        fetch(`${API_BASE}/appState`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ active: false }),
          keepalive: true,
        }).catch(() => {});
        return;
      }
      await apiCall('/appState', { method: 'POST', body: { active } });
    } catch { /* best-effort */ }
  }

  async sitOut(tableId: string): Promise<{ success: boolean; error?: string }> {
    console.log(`⏸️ [GameAPI] Sitting out at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean }>('/sitOut', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sit out',
      };
    }
  }

  /**
   * Return to table after sitting out
   */
  async sitIn(tableId: string): Promise<{ success: boolean; error?: string }> {
    console.log(`▶️ [GameAPI] Sitting in at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean }>('/sitIn', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sit in',
      };
    }
  }

  /**
   * Change seat at table
   */
  async changeSeat(
    tableId: string,
    newSeatIndex: number
  ): Promise<{ success: boolean; error?: string }> {
    console.log(`🔄 [GameAPI] Changing to seat ${newSeatIndex} at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean }>('/changeSeat', {
        method: 'POST',
        body: { tableId, seatIndex: newSeatIndex },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to change seat',
      };
    }
  }

  // ==========================================================================
  // GAME STATE OPERATIONS
  // ==========================================================================

  /**
   * Start new hand (owner only)
   */
  async startHand(tableId: string): Promise<{ success: boolean; error?: string; gameState?: any }> {
    console.log(`🎬 [GameAPI] Starting new hand at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean; gameState?: any }>('/startHand', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start hand',
      };
    }
  }

  /**
   *  Resolve all-in showdown after frontend animation completes

   * Called by PokerTable after runout suspense animation is done
   */
  async resolveAllInShowdown(tableId: string): Promise<{ 
    success: boolean; 
    error?: string; 
    gameState?: any;
    winners?: any[];
  }> {
    console.log(`🎰 [GameAPI] Resolving all-in showdown at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean; gameState?: any; winners?: any[] }>('/resolveAllInShowdown', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resolve showdown',
      };
    }
  }

  /**
   *  Trigger bomb pot for next hand (host only)

   */
  async triggerBombPot(tableId: string, anteMultiplier?: number): Promise<{ success: boolean; error?: string; bombPotAnte?: number }> {
    console.log(`💣 [GameAPI] Triggering bomb pot at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean; bombPotAnte?: number }>('/triggerBombPot', {
        method: 'POST',
        body: anteMultiplier ? { tableId, anteMultiplier } : { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to trigger bomb pot',
      };
    }
  }

  /**
   *  Post straddle (UTG player, 2x BB)

   */
  async postStraddle(tableId: string, _amount?: number): Promise<{ success: boolean; error?: string; straddleAmount?: number }> {
    console.log(`📈 [GameAPI] Posting straddle at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean; straddleAmount?: number }>('/postStraddle', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to post straddle',
      };
    }
  }

  /**
   *  Respond to Run It Twice offer

   */
  // Run It Multiple Times: runs is the count this player agrees to
  // (1 = run it once / decline, 2 or 3 = run it that many times). The
  // table runs the minimum everyone picked. Booleans still accepted for
  // back-compat (true = 2, false = 1).
  async respondToRunItTwice(tableId: string, runs: number | boolean): Promise<{
    success: boolean;
    error?: string;
    allAccepted?: boolean;
    declined?: boolean;
    agreedRuns?: number;
    waiting?: boolean;
    winners?: any[];
  }> {
    const runCount = typeof runs === 'boolean' ? (runs ? 2 : 1) : runs;
    console.log(`🔄 [GameAPI] RIT pick at table ${tableId}: ${runCount}x`);

    try {
      return await apiCall<any>('/respondToRunItTwice', {
        method: 'POST',
        body: { tableId, runs: runCount },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to respond to Run It Twice',
      };
    }
  }

  /**
   *  Resolve Run It Twice after frontend animation completes

   * Called after both runouts have been animated with equity percentages
   */
  async resolveRunItTwice(tableId: string): Promise<{ 
    success: boolean; 
    error?: string; 
    winners?: any[];
    runItTwiceResults?: any;
    rakeCollected?: number;
  }> {
    console.log(`🔄 [GameAPI] Resolving RIT at table ${tableId}`);

    try {
      return await apiCall<{ 
        success: boolean; 
        winners?: any[];
        runItTwiceResults?: any;
        rakeCollected?: number;
      }>('/resolveRunItTwice', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resolve Run It Twice',
      };
    }
  }

  /**
   * Force next hand (admin/owner recovery)
   */
  async forceNextHand(tableId: string): Promise<{ success: boolean; error?: string }> {
    console.log(`⏭️ [GameAPI] Forcing next hand at table ${tableId}`);

    try {
      return await apiCall<{ success: boolean }>('/forceNextHand', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to force next hand',
      };
    }
  }

  /**
   * Get current game state
   */
  async getGameState(tableId: string): Promise<{ success: boolean; gameState?: any; error?: string }> {
    try {
      return await apiCall<{ success: boolean; gameState?: any }>('/getGameState', {
        method: 'GET',
        params: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get game state',
      };
    }
  }

  // ==========================================================================
  // TABLE INFO & LIST
  // ==========================================================================

  /**
   * Get table info
   */
  async getTableInfo(tableId: string): Promise<{ success: boolean; table?: TableInfo; error?: string }> {
    try {
      return await apiCall<{ success: boolean; table?: TableInfo }>('/getTableInfo', {
        method: 'GET',
        params: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get table info',
      };
    }
  }

  /**
   * List available tables
   */
  async listTables(options?: {
    currency?: 'playcoins' | 'pkcash';
    minBlind?: number;
    maxBlind?: number;
  }): Promise<{ success: boolean; tables?: TableInfo[]; error?: string }> {
    try {
      const params: Record<string, string> = {};
      if (options?.currency) params.currency = options.currency;
      if (options?.minBlind) params.minBlind = options.minBlind.toString();
      if (options?.maxBlind) params.maxBlind = options.maxBlind.toString();

      return await apiCall<{ success: boolean; tables?: TableInfo[] }>('/listTables', {
        method: 'GET',
        params,
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to list tables',
      };
    }
  }

  // ==========================================================================
  // SYNC OPERATIONS
  // ==========================================================================

  /**
   * Sync table chips to wallet
   */
  async syncTableChips(tableId: string): Promise<{ success: boolean; chips?: number; error?: string }> {
    console.log(`🔄 [GameAPI] Syncing chips for table ${tableId}`);

    try {
      return await apiCall<{ success: boolean; chips?: number }>('/syncTableChips', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sync chips',
      };
    }
  }

  /**
   * Get wallet with table chips
   */
  async getWalletWithTableChips(): Promise<{
    success: boolean;
    playCoins?: number;
    pkCash?: number;
    tableChips?: Record<string, { chips: number; currency: string }>;
    error?: string;
  }> {
    try {
      return await apiCall<{
        success: boolean;
        playCoins?: number;
        pkCash?: number;
        tableChips?: Record<string, { chips: number; currency: string }>;
      }>('/getWalletWithTableChips', {
        method: 'GET',
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get wallet',
      };
    }
  }

  // ==========================================================================
  // TOURNAMENT OPERATIONS
  // ==========================================================================

  /**
   * Register for a tournament (server handles wallet deduction)
   */
  async registerTournament(tournamentId: string, inviteCode?: string): Promise<{
    success: boolean;
    error?: string;
    transactionId?: string;
  }> {
    console.log(`🎟️ [GameAPI] Registering for tournament ${tournamentId}`);

    try {
      return await apiCall<{ success: boolean; transactionId?: string }>('/registerTournament', {
        method: 'POST',
        body: { tournamentId, ...(inviteCode ? { inviteCode } : {}), stateCode: geoService.getCachedResult()?.region },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to register for tournament',
      };
    }
  }

  /**
   * Unregister from a tournament (server handles wallet refund)
   */
  async unregisterTournament(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    refundAmount?: number;
  }> {
    console.log(`🚪 [GameAPI] Unregistering from tournament ${tournamentId}`);

    try {
      return await apiCall<{ success: boolean; refundAmount?: number }>('/unregisterTournament', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to unregister from tournament',
      };
    }
  }

  /**
   * Tournament rebuy (server handles wallet deduction)
   */
  async tournamentRebuy(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    newChips?: number;
  }> {
    console.log(`💰 [GameAPI] Tournament rebuy for ${tournamentId}`);

    try {
      return await apiCall<{ success: boolean; newChips?: number }>('/tournamentRebuy', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to rebuy',
      };
    }
  }

  // ==========================================================================
  // TOURNAMENT ADMIN OPERATIONS (Server handles all wallet transactions)
  // ==========================================================================

  /**
   * Cancel tournament and refund all players (admin only)
   * Server handles: validation, bulk refunds, tournament status update atomically
   */
  async cancelTournament(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    refundCount?: number;
    totalRefunded?: number;
  }> {
    console.log(`❌ [GameAPI] Cancelling tournament ${tournamentId}`);

    try {
      return await apiCall<{ 
        success: boolean; 
        refundCount?: number; 
        totalRefunded?: number;
      }>('/cancelTournament', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to cancel tournament',
      };
    }
  }

  /**
   * Award tournament prize (server handles wallet credit)
   * Server handles: validation, wallet credit, transaction logging atomically
   */
  async awardTournamentPrize(
    tournamentId: string,
    userId: string,
    position: number,
    prizeAmount: number
  ): Promise<{
    success: boolean;
    error?: string;
    transactionId?: string;
  }> {
    console.log(`🏆 [GameAPI] Awarding $${prizeAmount} prize to ${userId} (position ${position})`);

    try {
      return await apiCall<{ success: boolean; transactionId?: string }>('/awardTournamentPrize', {
        method: 'POST',
        body: { tournamentId, userId, position, prizeAmount },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to award prize',
      };
    }
  }

  /**
   * Complete tournament with final results (server handles all prize distribution)
   * Server handles: validation, all prize payouts, tournament finalization atomically
   */
  async completeTournament(
    tournamentId: string,
    results: { userId: string; position: number; prizeAmount: number }[]
  ): Promise<{
    success: boolean;
    error?: string;
    totalAwarded?: number;
    winnerId?: string;
  }> {
    console.log(`🎉 [GameAPI] Completing tournament ${tournamentId} with ${results.length} results`);

    try {
      return await apiCall<{ 
        success: boolean; 
        totalAwarded?: number;
        winnerId?: string;
      }>('/completeTournament', {
        method: 'POST',
        body: { tournamentId, results },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to complete tournament',
      };
    }
  }

  /**
   * Collect tournament rake and distribute to agents (server handles all wallet credits)
   * Server handles: rake calculation, all agent payouts, transaction logging atomically
   */
  async collectTournamentRake(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    totalRake?: number;
    totalAgentPayout?: number;
    houseRakeNet?: number;
    agentPayouts?: { agentId: string; amount: number }[];
  }> {
    console.log(`💰 [GameAPI] Collecting tournament rake for ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        totalRake?: number;
        totalAgentPayout?: number;
        houseRakeNet?: number;
        agentPayouts?: { agentId: string; amount: number }[];
      }>('/collectTournamentRake', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to collect tournament rake',
      };
    }
  }

  /**
   * Update game state on server
   * Used for syncing state changes like showing cards
   */
  async updateGameState(
    tableId: string,
    updates: Record<string, any>
  ): Promise<{ success: boolean; error?: string }> {
    console.log(`📝 [GameAPI] Updating game state for table ${tableId}`);

    try {
      return await apiCall<{ success: boolean }>('/updateGameState', {
        method: 'POST',
        body: { tableId, updates },
      });
    } catch (error) {
      console.error(`❌ [GameAPI] Update game state failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update game state',
      };
    }
  }

  // ==========================================================================
  //  TABLE RESET - Emergency reset with veto system

  // ==========================================================================

  /**
   * Host initiates a table reset - starts 30 second voting period
   */
  async initiateTableReset(tableId: string): Promise<{
    success: boolean;
    pendingReset?: any;
    message?: string;
    error?: string;
  }> {
    console.log(`🔄 [GameAPI] Initiating table reset: ${tableId}`);

    try {
      return await apiCall('/initiateTableReset', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      console.error(`❌ [GameAPI] Initiate reset failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initiate reset',
      };
    }
  }

  /**
   * Player votes to approve or veto the reset
   */
  async voteOnReset(tableId: string, approve: boolean): Promise<{
    success: boolean;
    status?: 'vetoed' | 'waiting' | 'reset_complete';
    message?: string;
    error?: string;
  }> {
    console.log(`🗳️ [GameAPI] Voting on reset: ${tableId}, approve=${approve}`);

    try {
      return await apiCall('/voteOnReset', {
        method: 'POST',
        body: { tableId, approve },
      });
    } catch (error) {
      console.error(`❌ [GameAPI] Vote on reset failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to vote',
      };
    }
  }

  /**
   * Host executes reset after voting period ends (if no vetoes)
   */
  async executeTableReset(tableId: string): Promise<{
    success: boolean;
    status?: 'vetoed' | 'reset_complete';
    refundedPot?: number;
    message?: string;
    error?: string;
  }> {
    console.log(`⚡ [GameAPI] Executing table reset: ${tableId}`);

    try {
      return await apiCall('/executeTableReset', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      console.error(`❌ [GameAPI] Execute reset failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to execute reset',
      };
    }
  }

  // ==========================================================================
  //  TOURNAMENT STATE MACHINE - New Production-Ready Endpoints

  // ==========================================================================

  /**
   * Advance tournament to a new state (enforces valid transitions)
   */
  async advanceTournamentState(
    tournamentId: string,
    targetState: string,
    metadata?: Record<string, any>
  ): Promise<{
    success: boolean;
    previousState?: string;
    newState?: string;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Advancing tournament ${tournamentId} to ${targetState}`);

    try {
      return await apiCall<{
        success: boolean;
        previousState?: string;
        newState?: string;
      }>('/advanceTournamentState', {
        method: 'POST',
        body: { tournamentId, targetState, metadata },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to advance tournament state',
      };
    }
  }

  /**
   * Start tournament - creates tables and assigns players
   */
  async startTournament(tournamentId: string): Promise<{
    success: boolean;
    tables?: string[];
    tableAssignments?: { [tableId: string]: string[] };
    playerCount?: number;
    alreadyStarted?: boolean;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Starting tournament ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        tables?: string[];
        tableAssignments?: { [tableId: string]: string[] };
        playerCount?: number;
        alreadyStarted?: boolean;
      }>('/startTournament', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start tournament',
      };
    }
  }

  /**
   * Eliminate a player from tournament (freeze-out format)
   */
  async eliminateTournamentPlayer(
    tournamentId: string,
    eliminatedPlayerId: string,
    tableId?: string,
    position?: number
  ): Promise<{
    success: boolean;
    position?: number;
    remainingPlayers?: number;
    tournamentComplete?: boolean;
    alreadyEliminated?: boolean;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Eliminating player ${eliminatedPlayerId.substring(0,8)} from tournament ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        position?: number;
        remainingPlayers?: number;
        tournamentComplete?: boolean;
        alreadyEliminated?: boolean;
      }>('/eliminateTournamentPlayer', {
        method: 'POST',
        body: { tournamentId, eliminatedPlayerId, tableId, position },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to eliminate player',
      };
    }
  }

  /**
   * Advance tournament to next blind level
   */
  async advanceBlindLevel(tournamentId: string): Promise<{
    success: boolean;
    previousLevel?: number;
    newLevel?: number;
    blinds?: { smallBlind: number; bigBlind: number; ante: number };
    lateRegClosed?: boolean;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Advancing blind level for tournament ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        previousLevel?: number;
        newLevel?: number;
        blinds?: { smallBlind: number; bigBlind: number; ante: number };
        lateRegClosed?: boolean;
      }>('/advanceBlindLevel', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to advance blind level',
      };
    }
  }

  /**
   * Balance players across tournament tables (TDA rules)
   */
  async balanceTournamentTables(tournamentId: string): Promise<{
    success: boolean;
    moves?: { playerId: string; fromTable: string; toTable: string }[];
    tablesBalanced?: number;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Balancing tables for tournament ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        moves?: { playerId: string; fromTable: string; toTable: string }[];
        tablesBalanced?: number;
      }>('/balanceTournamentTables', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to balance tables',
      };
    }
  }

  /**
   * Break a tournament table and redistribute players
   */
  async breakTournamentTable(tournamentId: string, tableId: string): Promise<{
    success: boolean;
    brokenTable?: string;
    playersMoved?: { playerId: string; toTable: string }[];
    remainingTables?: number;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Breaking table ${tableId} in tournament ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        brokenTable?: string;
        playersMoved?: { playerId: string; toTable: string }[];
        remainingTables?: number;
      }>('/breakTournamentTable', {
        method: 'POST',
        body: { tournamentId, tableId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to break table',
      };
    }
  }

  /**
   * Get tournament prize structure based on current entries
   */
  async getTournamentPrizes(tournamentId: string): Promise<{
    success: boolean;
    playerCount?: number;
    totalPool?: number;
    rake?: number;
    prizePool?: number;
    currency?: string;
    payoutStructure?: { position: number; percentage: number; amount: number }[];
    paidPositions?: number;
    error?: string;
  }> {
    console.log(`🏆 [GameAPI] Getting prize structure for tournament ${tournamentId}`);

    try {
      return await apiCall<{
        success: boolean;
        playerCount?: number;
        totalPool?: number;
        rake?: number;
        prizePool?: number;
        currency?: string;
        payoutStructure?: { position: number; percentage: number; amount: number }[];
        paidPositions?: number;
      }>('/getTournamentPrizes', {
        method: 'POST',
        body: { tournamentId },
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get prize structure',
      };
    }
  }

  // ==========================================================================
  //  TOURNAMENT REBUY / ADD-ON

  // ==========================================================================
  async tournamentRebuy(tournamentId: string, tableId?: string): Promise<{
    success: boolean;
    error?: string;
    newChips?: number;
    newPrizePool?: number;
    totalRebuys?: number;
    tableId?: string;
  }> {
    try {
      const data = await apiCall<any>('/tournamentRebuy', {
        method: 'POST',
        // tableId optional: the server resolves the player's table when the
        // client doesn't know it (stale caches after a bust).
        body: tableId ? { tournamentId, tableId } : { tournamentId },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Rebuy failed' };
    }
  }

  async tournamentAddOn(tournamentId: string, tableId: string): Promise<{
    success: boolean;
    error?: string;
    addOnChips?: number;
    addOnCost?: number;
    newPrizePool?: number;
  }> {
    try {
      const data = await apiCall<any>('/tournamentAddOn', {
        method: 'POST',
        body: { tournamentId, tableId },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Add-on failed' };
    }
  }

  // ==========================================================================
  //  SKILL CARD

  // ==========================================================================
  async getSkillCard(userId?: string): Promise<{
    success: boolean;
    error?: string;
    userId?: string;
    username?: string;
    photoURL?: string | null;
    sinceDate?: number | null;
    hasStats?: boolean;
    rawStats?: {
      totalTournaments: number;
      totalBuyIns: number;
      totalWinnings: number;
      itmCount: number;
      finalTableCount: number;
      firstPlaceCount: number;
      biggestScore: number;
    };
    scores?: {
      roi: number; itm: number; winnings: number;
      tournamentsPlayed: number; finalTables: number;
      firstRank: number; biggestScore: number;
    };
    overallSkillScore?: number;
    worldRank?: number | null;
    sampleSize?: number;
  }> {
    try {
      const data = await apiCall<any>('/skillCard', {
        method: 'GET',
        params: userId ? { userId } : undefined,
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load skill card' };
    }
  }

  async setSkillCardPrivacy(isPublic: boolean): Promise<{ success: boolean; error?: string; publicSkillCard?: boolean }> {
    try {
      const data = await apiCall<any>('/skillCard/setPrivacy', {
        method: 'POST',
        body: { isPublic },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update privacy' };
    }
  }

  // ==========================================================================
  //  SPECTATOR

  // ==========================================================================
  async joinAsSpectator(tableId: string, username: string, photoURL?: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const fn = httpsCallable<
        { tableId: string; username?: string; photoURL?: string },
        { success: boolean; error?: string; message?: string }
      >(functions, 'joinAsSpectator');
      const res = await fn({ tableId, username, photoURL });
      return res.data;
    } catch (error: any) {
      return { success: false, error: error?.message || 'Failed to join as spectator' };
    }
  }

  async leaveSpectator(tableId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const fn = httpsCallable<{ tableId: string }, { success: boolean; error?: string }>(functions, 'leaveSpectator');
      const res = await fn({ tableId });
      return res.data;
    } catch (error: any) {
      return { success: false, error: error?.message || 'Failed to leave as spectator' };
    }
  }

  // ==========================================================================
  //  USER SEARCH (used by admin panels + spectator player picker)

  // ==========================================================================
  async searchUsers(query: string, limit = 10): Promise<{
    success: boolean;
    error?: string;
    users?: Array<{ id: string; username: string; photoURL?: string | null; isFriend?: boolean }>;
  }> {
    try {
      const data = await apiCall<any>('/searchUsers', {
        method: 'GET',
        params: { q: query, limit: String(limit) },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Search failed' };
    }
  }

  // ==========================================================================
  //  ADMIN - TOURNAMENT MANAGEMENT (force-start, blinds, balance, cancel)

  // ==========================================================================
  async adminForceFoldCurrentPlayer(tableId: string): Promise<{
    success: boolean;
    error?: string;
    foldedPlayerId?: string;
    foldedPlayerName?: string | null;
    nextPlayerIndex?: number;
  }> {
    try {
      const data = await apiCall<any>('/admin/forceFoldCurrentPlayer', {
        method: 'POST',
        body: { tableId },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Force fold failed' };
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // WATCH INVITES (friends & family spectating)
  // ──────────────────────────────────────────────────────────────────
  // Host generates a single-use 8-char code; a friend redeems it to
  // spectate the host's current table for the duration of the session.
  // Server enforces the concurrent-watcher cap and 1h code expiry.
  async createWatchInvite(): Promise<{
    success: boolean;
    error?: string;
    inviteCode?: string;
    expiresAt?: string;
    maxWatchers?: number;
    currentWatchers?: number;
  }> {
    try {
      const data = await apiCall<any>('/watch/createInvite', { method: 'POST', body: {} });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Create invite failed' };
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // TOURNAMENT TRAINING CHALLENGES
  // ──────────────────────────────────────────────────────────────────
  async getTournamentChallengeState(): Promise<{
    success: boolean;
    error?: string;
    claims?: Record<string, any>;
  }> {
    try {
      const data = await apiCall<any>('/tournamentChallenges/state', { method: 'POST', body: {} });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Load failed' };
    }
  }

  async claimTournamentChallenge(
    challengeId: string,
    answers: Array<{ stepId: string; action: string; evaluation: string }>,
    stateCode?: string
  ): Promise<{
    success: boolean;
    error?: string;
    alreadyClaimed?: boolean;
    rewardPkCash?: number;
    geoRestricted?: boolean;
    playCoins?: number;
    points?: number;
  }> {
    try {
      const data = await apiCall<any>('/claimTournamentChallenge', {
        method: 'POST',
        body: { challengeId, answers, stateCode },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Claim failed' };
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // CHEAT / BOT DETECTION (admin)
  // ──────────────────────────────────────────────────────────────────
  async adminRunCheatScan(): Promise<{
    success: boolean;
    error?: string;
    scanned?: number;
    flaggedCount?: number;
    flags?: any[];
  }> {
    try {
      const data = await apiCall<any>('/admin/runCheatScan', { method: 'POST', body: {} });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Scan failed' };
    }
  }

  async adminGetCheatFlags(): Promise<{
    success: boolean;
    error?: string;
    flags?: any[];
  }> {
    try {
      const data = await apiCall<any>('/admin/getCheatFlags', { method: 'POST', body: {} });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Load failed' };
    }
  }

  async adminResolveCheatFlag(
    uid: string,
    action: 'ban' | 'watchlist' | 'dismiss',
    reason?: string,
    banDurationHours?: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const data = await apiCall<any>('/admin/resolveCheatFlag', {
        method: 'POST',
        body: { uid, action, reason, banDurationHours },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Action failed' };
    }
  }

  async adminStartTournament(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    tables?: any[];
    playerCount?: number;
  }> {
    try {
      const data = await apiCall<any>('/startTournament', {
        method: 'POST',
        body: { tournamentId },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Start failed' };
    }
  }

  async adminAdvanceBlindLevel(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    previousLevel?: number;
    newLevel?: number;
    blinds?: { smallBlind: number; bigBlind: number; ante?: number };
  }> {
    try {
      const data = await apiCall<any>('/advanceBlindLevel', {
        method: 'POST',
        body: { tournamentId },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Advance failed' };
    }
  }

  async adminTournamentSimulate(tournamentId: string, action: 'startBreak' | 'endBreak' | 'bustBots', count?: number): Promise<{
    success: boolean;
    error?: string;
    message?: string;
  }> {
    try {
      const data = await apiCall<any>('/admin/tournamentSimulate', {
        method: 'POST',
        body: { tournamentId, action, ...(count ? { count } : {}) },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Simulate failed' };
    }
  }

  async adminBalanceTables(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
    moves?: any[];
    tablesBalanced?: number;
  }> {
    try {
      const data = await apiCall<any>('/balanceTournamentTables', {
        method: 'POST',
        body: { tournamentId },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Balance failed' };
    }
  }

  async adminCancelTournament(tournamentId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const data = await apiCall<any>('/cancelTournament', {
        method: 'POST',
        body: { tournamentId },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Cancel failed' };
    }
  }

  // ==========================================================================
  //  EMULATOR ONLY - Bootstrap calling user as admin + verified

  // ==========================================================================
  async emulatorBootstrap(): Promise<{
    success: boolean;
    error?: string;
    userId?: string;
    email?: string | null;
  }> {
    try {
      const data = await apiCall<any>('/admin/emulatorBootstrap', {
        method: 'POST',
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Bootstrap failed' };
    }
  }

  // ==========================================================================
  //  ADMIN - EMAIL VERIFICATION BYPASS

  // ==========================================================================
  async adminVerifyUser(targetUserId?: string): Promise<{
    success: boolean;
    error?: string;
    alreadyVerified?: boolean;
    userId?: string;
    email?: string | null;
  }> {
    try {
      const data = await apiCall<any>('/admin/verifyUser', {
        method: 'POST',
        body: targetUserId ? { targetUserId } : {},
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Verify failed' };
    }
  }

  // ==========================================================================
  //  ADMIN - PUSH NOTIFICATION TEST

  // ==========================================================================
  async getUserPushTokens(userId: string): Promise<{
    success: boolean;
    error?: string;
    username?: string | null;
    pushNotificationsEnabled?: boolean;
    legacyToken?: { token: string; platform: string; updatedAt: number | null } | null;
    tokens?: Array<{ token: string; platform: string; active: boolean; createdAt: number | null; updatedAt: number | null }>;
  }> {
    try {
      const data = await apiCall<any>('/admin/userPushTokens', {
        method: 'GET',
        params: { userId },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load tokens' };
    }
  }

  /** Users with a registered device, newest first - the pick-list for push testing. */
  async pushRecipients(limit = 40): Promise<{
    success: boolean;
    error?: string;
    recipients?: Array<{
      id: string;
      username: string | null;
      email: string | null;
      platform: string;
      pushEnabled: boolean;
      registeredAt: number | null;
      deviceCount: number;
    }>;
  }> {
    try {
      const data = await apiCall<any>(`/admin/pushRecipients?limit=${limit}`, { method: 'GET' });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load recipients' };
    }
  }

  // Full evidence dossier for one player (admin) - identity, ledger,
  // security flags, devices, and recent hands with full action logs.
  // 🏠 HOME GAME: host assigns session chips (host-banked tables only)
  async hostKickPlayer(tableId: string, targetUserId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/hostKickPlayer', { method: 'POST', body: { tableId, targetUserId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Kick failed' }; }
  }

  async hostPauseGame(tableId: string, paused: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/hostPauseGame', { method: 'POST', body: { tableId, paused } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Pause failed' }; }
  }

  async hostSetBlinds(tableId: string, smallBlind: number, bigBlind: number): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/hostSetBlinds', { method: 'POST', body: { tableId, smallBlind, bigBlind } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Blinds change failed' }; }
  }

  async hostEndGame(tableId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/hostEndGame', { method: 'POST', body: { tableId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'End game failed' }; }
  }

  async hostAssignChips(tableId: string, targetUserId: string, amount: number): Promise<{ success: boolean; newChips?: number; error?: string }> {
    try {
      return await apiCall<{ success: boolean; newChips?: number }>('/hostAssignChips', {
        method: 'POST',
        body: { tableId, targetUserId, amount },
      });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Assign failed' };
    }
  }

  // ===== 👥 GROUPS =====

  async createGroup(name: string, description: string, isPublic: boolean): Promise<{ success: boolean; groupId?: string; joinCode?: string | null; error?: string }> {
    try {
      return await apiCall<any>('/createGroup', { method: 'POST', body: { name, description, isPublic } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Create failed' }; }
  }

  async updateGroup(groupId: string, patch: { name?: string; description?: string; coverUrl?: string | null; coverTheme?: number }): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/updateGroup', { method: 'POST', body: { groupId, ...patch } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Update failed' }; }
  }

  async groupInvite(groupId: string, userIds: string[]): Promise<{ success: boolean; invited?: number; alreadyIn?: number; error?: string }> {
    try {
      return await apiCall<any>('/groupInvite', { method: 'POST', body: { groupId, userIds } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Invite failed' }; }
  }

  async joinGroup(opts: { groupId?: string; code?: string; inviteId?: string }): Promise<{ success: boolean; groupId?: string; requested?: boolean; error?: string }> {
    try {
      return await apiCall<any>('/joinGroup', { method: 'POST', body: opts });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Join failed' }; }
  }

  async leaveGroup(groupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/leaveGroup', { method: 'POST', body: { groupId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Leave failed' }; }
  }

  async deleteGroup(groupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/deleteGroup', { method: 'POST', body: { groupId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Delete failed' }; }
  }

  async searchGroups(q: string): Promise<{ success: boolean; groups?: any[]; error?: string }> {
    try {
      return await apiCall<any>('/searchGroups', { method: 'GET', params: { q } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Search failed' }; }
  }

  async getMyGroups(): Promise<{ success: boolean; groups?: any[]; error?: string }> {
    try {
      return await apiCall<any>('/myGroups', { method: 'GET' });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Load failed' }; }
  }

  async getSuggestedGroups(): Promise<{ success: boolean; groups?: any[]; error?: string }> {
    try {
      return await apiCall<any>('/suggestedGroups', { method: 'GET' });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Load failed' }; }
  }

  async getGroupDetail(groupId: string): Promise<{ success: boolean; group?: any; members?: any[]; events?: any[]; error?: string }> {
    try {
      return await apiCall<any>('/groupDetail', { method: 'GET', params: { groupId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Load failed' }; }
  }

  async groupInviteNow(groupId: string, tableId: string, note?: string, inviteeIds?: string[]): Promise<{ success: boolean; notified?: number; error?: string }> {
    try {
      return await apiCall<any>('/groupInviteNow', { method: 'POST', body: { groupId, tableId, note, inviteeIds } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Invite failed' }; }
  }

  async scheduleGroupGame(groupId: string, scheduledFor: number, note: string, tableConfig: { currency: string; smallBlind: number; bigBlind: number; maxSeats: number }, inviteeIds?: string[]): Promise<{ success: boolean; eventId?: string; invited?: number; error?: string }> {
    try {
      return await apiCall<any>('/scheduleGroupGame', { method: 'POST', body: { groupId, scheduledFor, note, tableConfig, inviteeIds } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Schedule failed' }; }
  }

  // Join requests (private groups), roster, roles
  async groupRequestAction(groupId: string, userId: string, action: 'accept' | 'decline'): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/groupRequestAction', { method: 'POST', body: { groupId, userId, action } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Action failed' }; }
  }

  async cancelGroupRequest(groupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/cancelGroupRequest', { method: 'POST', body: { groupId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Cancel failed' }; }
  }

  async groupMembers(groupId: string, offset = 0, limit = 50): Promise<{ success: boolean; members?: GroupMemberCard[]; total?: number; nextOffset?: number | null; error?: string }> {
    try {
      return await apiCall<any>('/groupMembers', { method: 'GET', params: { groupId, offset: String(offset), limit: String(limit) } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Load failed' }; }
  }

  async groupMemberAction(groupId: string, userId: string, action: 'remove' | 'promote' | 'demote'): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/groupMemberAction', { method: 'POST', body: { groupId, userId, action } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Action failed' }; }
  }

  // Group chat (server-backed; the page polls /groupChat/messages)
  async groupChatSend(groupId: string, text: string, mentions: string[] = []): Promise<{ success: boolean; message?: GroupChatMessage; error?: string }> {
    try {
      return await apiCall<any>('/groupChat/send', { method: 'POST', body: { groupId, text, mentions } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Send failed' }; }
  }

  async groupChatMessages(groupId: string, opts: { after?: number; before?: number; limit?: number } = {}): Promise<{ success: boolean; messages?: GroupChatMessage[]; hasMore?: boolean; serverTime?: number; role?: string | null; error?: string }> {
    try {
      const params: Record<string, string> = { groupId };
      if (opts.after) params.after = String(opts.after);
      if (opts.before) params.before = String(opts.before);
      if (opts.limit) params.limit = String(opts.limit);
      return await apiCall<any>('/groupChat/messages', { method: 'GET', params, timeoutMs: 20000 });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Load failed' }; }
  }

  async groupChatDelete(groupId: string, messageId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/groupChat/delete', { method: 'POST', body: { groupId, messageId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Delete failed' }; }
  }

  async groupChatMarkRead(groupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/groupChat/markRead', { method: 'POST', body: { groupId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Failed' }; }
  }

  async registerGroupEvent(eventId: string, register: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/registerGroupEvent', { method: 'POST', body: { eventId, register } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Register failed' }; }
  }

  async cancelGroupEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/cancelGroupEvent', { method: 'POST', body: { eventId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Cancel failed' }; }
  }

  async startGroupEvent(eventId: string, tableId: string): Promise<{ success: boolean; notified?: number; error?: string }> {
    try {
      return await apiCall<any>('/startGroupEvent', { method: 'POST', body: { eventId, tableId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Start failed' }; }
  }

  // 🏠 HOME GAME: seated player asks the host for chips (no wallet involved)
  async requestSessionChips(tableId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/requestSessionChips', { method: 'POST', body: { tableId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Request failed' }; }
  }

  async hostDismissChipRequest(tableId: string, targetUserId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<any>('/hostDismissChipRequest', { method: 'POST', body: { tableId, targetUserId } });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Dismiss failed' }; }
  }

  async joinWaitlist(tableId: string): Promise<{ success: boolean; position?: number; error?: string }> {
    try {
      return await apiCall<{ success: boolean; position?: number }>('/joinWaitlist', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Waitlist failed' };
    }
  }

  async leaveWaitlist(tableId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await apiCall<{ success: boolean }>('/leaveWaitlist', {
        method: 'POST',
        body: { tableId },
      });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Waitlist failed' };
    }
  }

  // 🏠 Archived settle-up sheets for closed home games
  async getMyHomeGameLedgers(): Promise<{
    success: boolean;
    error?: string;
    ledgers?: Array<{ tableId: string; tableName: string; ownerId: string | null; rows: any[]; closedAt: number | null; reason: string | null }>;
  }> {
    try {
      return await apiCall<any>('/myHomeGameLedgers', { method: 'GET' });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Ledgers failed' };
    }
  }

  // Player Stats page: own aggregates + 7-day results + recent hand results
  async getMyStats(): Promise<{
    success: boolean;
    error?: string;
    stats?: { handsPlayed: number; vpipPct: number | null; showdownWinPct: number | null };
    byCurrency?: Record<'playcoins' | 'pkcash', {
      days: Array<{ label: string; net: number }>;
      recent: Array<{ handNumber: number | null; amount: number; ts: number }>;
      biggestWin: number;
      weekNet: number;
    }>;
  }> {
    try {
      return await apiCall<any>('/myStats', { method: 'GET' });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Stats failed' };
    }
  }

  async getPlayerDossier(userId: string): Promise<{
    success: boolean;
    error?: string;
    dossier?: any;
  }> {
    try {
      const data = await apiCall<any>('/admin/playerDossier', {
        method: 'GET',
        params: { userId },
      });
      return { success: true, dossier: data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Dossier failed' };
    }
  }

  async sendTestPush(targetUserId: string, title: string, body: string, opts?: {
    type?: string;
    data?: Record<string, string>;
  }): Promise<{
    success: boolean;
    error?: string;
    reason?: string;
    message?: string;
    tokensTried?: number;
    successCount?: number;
    failureCount?: number;
    invalidatedCount?: number;
    report?: Array<{ token: string; success: boolean; errorCode: string | null; messageId: string | null }>;
  }> {
    try {
      const data = await apiCall<any>('/admin/sendTestPush', {
        method: 'POST',
        body: {
          targetUserId,
          title,
          body,
          type: opts?.type,
          data: opts?.data,
        },
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Send failed' };
    }
  }

  // ==========================================================================
  //  ADMIN - TOURNAMENT TEST UTILITIES

  // ==========================================================================
  async deleteAllTestTournaments(): Promise<{
    success: boolean;
    error?: string;
    deleted?: number;
    refunded?: number;
    tablesEnded?: number;
    message?: string;
  }> {
    try {
      const data = await apiCall<any>('/admin/deleteAllTestTournaments', {
        method: 'POST',
        body: {},
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Cleanup failed' };
    }
  }

  async generateDailyCatalog(opts: { date?: string; days?: number } = {}): Promise<{
    success: boolean;
    error?: string;
    totalCreated?: number;
    totalSkipped?: number;
    deletedZombies?: number;
    perDay?: Array<{ date: string; created: number; skipped: number }>;
  }> {
    try {
      const data = await apiCall<any>('/admin/generateDailyCatalog', {
        method: 'POST',
        body: opts,
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to generate daily catalog' };
    }
  }

  /**
   * Weekly tier-ladder tournaments (the recurring headline events), created
   * SERVER-side. The tournamentService equivalent builds them from the
   * browser, which needs client write access to the tournaments collection -
   * this endpoint does it with the admin SDK instead.
   */
  async generateWeeklyTournaments(): Promise<{
    success: boolean;
    error?: string;
    created?: number;
    skipped?: number;
    message?: string;
  }> {
    try {
      const data = await apiCall<any>('/generateWeeklyTournaments', {
        method: 'POST',
        body: {},
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to generate weekly tournaments' };
    }
  }

  /** Fire the premade Pack Drop push. testOnly sends to the calling admin only. */
  async packDropNotify(opts: { testOnly?: boolean; variant?: string } = {}): Promise<{
    success: boolean; error?: string; sentTo?: number; candidates?: number;
    failed?: number; testOnly?: boolean; reason?: string | null;
    message?: { title: string; body: string };
  }> {
    try {
      const data = await apiCall<any>('/admin/packDropNotify', { method: 'POST', body: opts });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to send' };
    }
  }

  /**
   * Is the mail transport actually alive? Verifies the SMTP connection rather
   * than reporting on the presence of a config value.
   */
  async emailStatus(): Promise<{
    success: boolean; error?: string;
    configured?: boolean; verified?: boolean; from?: string; detail?: string;
    provider?: 'sendgrid' | 'gmail' | 'none';
  }> {
    try {
      const data = await apiCall<any>('/admin/emailStatus', { method: 'GET' });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to check email status' };
    }
  }

  /** Branded broadcast email. testOnly delivers to the calling admin only. */
  async emailAudience(): Promise<{
    success: boolean; error?: string;
    counts?: { all: number; active7: number; inactive14: number; new30: number; optedOut: number; noEmail: number };
  }> {
    try {
      return await apiCall<any>('/admin/emailAudience', { method: 'GET' });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Audience failed' }; }
  }

  /** Admin: top training earners with time-spent estimates and cheat flags. */
  async trainingEarnings(limit = 100): Promise<{
    success: boolean; error?: string; rows?: any[]; totalEarned?: number; flagged?: number; rules?: any;
  }> {
    try {
      return await apiCall<any>(`/admin/trainingEarnings?limit=${limit}`, { method: 'GET' });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Training audit failed' }; }
  }

  // ===== PARTNER BYPASS CODES (server-issued) =====
  async bypassCodeQuota(): Promise<any> {
    return apiCall<any>('/bypassCodes/quota', { method: 'GET' });
  }
  async bypassCodeCreate(description?: string): Promise<any> {
    return apiCall<any>('/bypassCodes/create', { method: 'POST', body: { description: description || '' } });
  }
  async bypassCodeCheck(code: string): Promise<any> {
    return apiCall<any>('/bypassCodes/check', { method: 'POST', body: { code } });
  }

  // ===== TABLE RECORDINGS =====
  async recordingsRules(tableId?: string): Promise<any> {
    return apiCall<any>(`/recordings/rules${tableId ? `?tableId=${encodeURIComponent(tableId)}` : ''}`, { method: 'GET' });
  }
  async recordingsStart(tableId: string, showCards: boolean = true): Promise<any> {
    return apiCall<any>('/recordings/start', { method: 'POST', body: { tableId, showCards } });
  }
  async recordingsShowCards(recordingId: string, show: boolean): Promise<any> {
    return apiCall<any>('/recordings/showCards', { method: 'POST', body: { recordingId, show } });
  }
  async recordingsOwnerCards(tableId: string): Promise<{ success: boolean; ownerUid: string | null; holeCards: any[]; showCards?: boolean }> {
    return apiCall<any>('/recordings/ownerCards', { method: 'POST', body: { tableId } });
  }
  async recordingsStop(recordingId: string): Promise<any> {
    return apiCall<any>('/recordings/stop', { method: 'POST', body: { recordingId } });
  }
  async recordingsMine(): Promise<any> {
    return apiCall<any>('/recordings/mine', { method: 'GET' });
  }
  async recordingsDelete(recordingId: string): Promise<any> {
    return apiCall<any>('/recordings/delete', { method: 'POST', body: { recordingId } });
  }
  async recordingsConsent(recordingId: string, accept: boolean): Promise<any> {
    return apiCall<any>('/recordings/consent', { method: 'POST', body: { recordingId, accept } });
  }
  async adminRecordings(): Promise<any> {
    return apiCall<any>('/admin/recordings', { method: 'GET' });
  }
  async adminRecordingDelete(recordingId: string): Promise<any> {
    return apiCall<any>('/admin/recordings/delete', { method: 'POST', body: { recordingId } });
  }

  async emailCampaigns(): Promise<{ success: boolean; error?: string; campaigns?: any[] }> {
    try {
      return await apiCall<any>('/admin/emailCampaigns', { method: 'GET' });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'History failed' }; }
  }

  /** Who a campaign (identified by its subject) reached; with audience, how many a resend would go to. */
  async emailCampaignRecipients(subject: string, audience?: string): Promise<{
    success: boolean; error?: string; campaignKey?: string; remaining?: number | null;
    counts?: Record<string, number>;
    recipients?: Array<{ uid: string; email: string; status: string; error: string | null; updatedAt: number | null }>;
  }> {
    try {
      const q = `subject=${encodeURIComponent(subject)}${audience ? `&audience=${encodeURIComponent(audience)}` : ''}`;
      return await apiCall<any>(`/admin/emailCampaignRecipients?${q}`, { method: 'GET' });
    } catch (e) { return { success: false, error: e instanceof Error ? e.message : 'Recipients failed' }; }
  }

  async emailBroadcast(opts: {
    subject: string; heading: string; body: string;
    ctaLabel?: string; ctaUrl?: string; preheader?: string;
    audience?: string; testOnly?: boolean; limit?: number;
  }): Promise<{
    success: boolean; error?: string; testOnly?: boolean; audience?: string;
    candidates?: number; skipped?: number; sentTo?: number; failed?: number; to?: string;
    notAttempted?: number; stopReason?: string | null;
  }> {
    try {
      // Long timeout: a campaign is paced under the provider's rate limit.
      const data = await apiCall<any>('/admin/emailBroadcast', { method: 'POST', body: opts, timeoutMs: 90000 });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to send broadcast' };
    }
  }

  /** One-time stats rebuild from retained hand histories (admin, System tab). */
  async rebuildPokerIntel(): Promise<{
    success: boolean; error?: string;
    handsScanned?: number; playersFound?: number; updated?: number; keptExisting?: number;
  }> {
    try {
      // Long timeout: this pages through every table's hand history.
      const data = await apiCall<any>('/admin/rebuildPokerIntel', {
        method: 'POST', body: {}, timeoutMs: 120000,
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Rebuild failed' };
    }
  }

  /**
   * The caller's OWN dealt cards for past hands at a table. Served from a
   * server-only collection - opponents' cards are not in the response and
   * not readable any other way.
   */
  async handOwnCards(tableId: string, handNumbers: number[]): Promise<{
    success: boolean; error?: string; cards?: Record<number, any[]>;
  }> {
    try {
      const data = await apiCall<any>('/handHistory/ownCards', {
        method: 'POST', body: { tableId, handNumbers }, retry: 1,
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Could not load your cards' };
    }
  }

  /** PSS avatar roster with the caller's unlocks and PlayCoin balance. */
  async avatarRoster(): Promise<{
    success: boolean; error?: string;
    roster?: Array<{ id: string; name: string; tier: string; price: number }>;
    owned?: string[]; playCoins?: number;
  }> {
    try {
      const data = await apiCall<any>('/avatars/roster', { method: 'GET', retry: 1 });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Could not load avatars' };
    }
  }

  /**
   * Unlock a PSS avatar with PlayCoins. Retry-safe: the server checks
   * ownership inside its transaction, so a repeat after a lost reply sees
   * the avatar owned and does not charge twice.
   */
  async purchaseAvatar(avatarId: string): Promise<{
    success: boolean; error?: string;
    alreadyOwned?: boolean; charged?: number; balance?: number;
  }> {
    try {
      const data = await apiCall<any>('/avatars/purchase', {
        method: 'POST', body: { avatarId }, retry: 2,
      });
      return { success: data.success !== false, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }

  async playerProfile(targetUserId: string): Promise<{
    success: boolean;
    error?: string;
    isBot?: boolean;
    name?: string | null;
    gameStats?: {
      handsPlayed: number;
      vpipPct: number | null;
      pfrPct: number | null;
      wtsdPct: number | null;
      wsdPct: number | null;
      netBB: number | null;
    } | null;
    career?: {
      played: number;
      cashed: number;
      cashRatePct: number;
      won: number;
      finalTables: number;
      totalWinnings: number;
      biggestScore: number;
      currency: string;
    } | null;
  }> {
    try {
      const data = await apiCall<any>('/playerProfile', {
        method: 'POST',
        body: { targetUserId },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load profile' };
    }
  }

  // ── Agent program (admin + manager) ──────────────────────────────────────
  async adminAgentPayouts(scope: 'open' | 'all' = 'open'): Promise<{ success: boolean; payouts: any[]; error?: string }> {
    try {
      const data = await apiCall<any>(`/admin/agentPayouts?status=${scope}`, { method: 'GET' });
      return { success: true, payouts: data.payouts || [] };
    } catch (error) {
      return { success: false, payouts: [], error: error instanceof Error ? error.message : 'Failed to load payouts' };
    }
  }

  async adminAgentPayoutAction(payoutId: string, action: 'approve' | 'paid' | 'sendUsdc' | 'verifyUsdc' | 'reject', note?: string, extra?: { payoutReference?: string; code?: string }): Promise<{ success: boolean; error?: string; needsVerification?: boolean; verified?: boolean; message?: string }> {
    try {
      const data = await apiCall<any>('/admin/agentPayoutAction', { method: 'POST', body: { payoutId, action, note, ...(extra || {}) } });
      return { success: true, needsVerification: !!data.needsVerification, verified: !!data.verified, message: data.message };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Payout action failed' };
    }
  }

  async adminAgentSettings(payload: { agentId: string; tier?: string; customCode?: string; managerId?: string | null; active?: boolean; overrideRakePercent?: number; canHostTournaments?: boolean; mediaAllowed?: 'none' | 'voice' | 'video'; mediaComped?: boolean }): Promise<{ success: boolean; error?: string }> {
    try {
      await apiCall<any>('/admin/agentSettings', { method: 'POST', body: payload });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update agent' };
    }
  }

  async adminCreateManager(payload: { userId: string; overrideRakePercent?: number; customCode?: string }): Promise<{ success: boolean; error?: string }> {
    try {
      await apiCall<any>('/admin/createManager', { method: 'POST', body: payload });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create manager' };
    }
  }

  // ── Payouts: destinations and admin execution (no processor) ──────────────
  async payoutDestinations(): Promise<{ success: boolean; destinations: Record<string, { value: string; network: string | null; holdUntil: string | null }>; usdcNetwork?: string; usdcPayoutsEnabled?: boolean; error?: string }> {
    try {
      return await apiCall<any>('/wallet/payoutDestinations', { method: 'GET' });
    } catch (error) {
      return { success: false, destinations: {}, error: error instanceof Error ? error.message : 'Could not load payout details' };
    }
  }

  async setPayoutDestination(method: 'usdc' | 'paypal', value: string): Promise<{ success: boolean; holdUntil?: string | null; error?: string }> {
    try {
      return await apiCall<any>('/wallet/setPayoutDestination', { method: 'POST', body: { method, value } });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Could not save' };
    }
  }

  async paymentOptions(): Promise<{ crypto: boolean; usdcPayouts: boolean; kyc: string }> {
    try {
      return await apiCall<any>('/payments/options', { method: 'GET' });
    } catch {
      return { crypto: false, usdcPayouts: false, kyc: 'none' };
    }
  }

  // ── Creator program: hosted tournaments and creator groups ──────────────
  async hostCreateTournament(payload: HostCreatePayload): Promise<{ success: boolean; tournamentId?: string; status?: string; inviteCode?: string | null; quota?: HostQuota; error?: string }> {
    try {
      return await apiCall<any>('/host/createTournament', { method: 'POST', body: payload });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Could not create the tournament' };
    }
  }

  async hostTournaments(): Promise<{ success: boolean; tournaments: HostTournamentRow[]; creatorGroup: CreatorGroupInfo | null; quota: HostQuota; permissions: HostPermissions; error?: string }> {
    try {
      return await apiCall<any>('/host/tournaments', { method: 'GET' });
    } catch (error) {
      return {
        success: false, tournaments: [], creatorGroup: null,
        quota: { usedThisMonth: 0, freePerMonth: 2, nextNeedsApproval: false },
        permissions: { canHost: false, isAdmin: false, mediaAllowed: 'none', mediaComped: false },
        error: error instanceof Error ? error.message : 'Could not load your tournaments',
      };
    }
  }

  async agentCreatorGroup(): Promise<{ success: boolean; group: CreatorGroupInfo | null; error?: string }> {
    try {
      return await apiCall<any>('/agent/creatorGroup', { method: 'GET' });
    } catch (error) {
      return { success: false, group: null, error: error instanceof Error ? error.message : 'Could not load your group' };
    }
  }

  async adminHostTournaments(status: 'pending' | 'all' = 'pending'): Promise<{ success: boolean; tournaments: HostTournamentRow[]; error?: string }> {
    try {
      return await apiCall<any>(`/admin/hostTournaments?status=${status}`, { method: 'GET' });
    } catch (error) {
      return { success: false, tournaments: [], error: error instanceof Error ? error.message : 'Could not load creator tournaments' };
    }
  }

  async adminHostTournamentAction(payload: { tournamentId: string; action: 'approve' | 'reject'; note?: string }): Promise<{ success: boolean; error?: string }> {
    try {
      await apiCall<any>('/admin/hostTournamentAction', { method: 'POST', body: payload });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Action failed' };
    }
  }

  async adminBackfillCreatorGroups(): Promise<{ success: boolean; groups?: number; playersAdded?: number; error?: string }> {
    try {
      return await apiCall<any>('/admin/backfillCreatorGroups', { method: 'POST', body: {} });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Backfill failed' };
    }
  }

  async managerAgents(): Promise<{ success: boolean; isManager: boolean; agents: any[]; overrideRakePercent?: number; totalOverrideEarned?: number; error?: string }> {
    try {
      return await apiCall<any>('/manager/agents', { method: 'GET' });
    } catch (error) {
      return { success: false, isManager: false, agents: [], error: error instanceof Error ? error.message : 'Failed to load team' };
    }
  }

  async adminGrantVoiceMinutes(targetUserId: string, minutes: number): Promise<{ success: boolean; error?: string }> {
    try {
      await apiCall<any>('/admin/grantVoiceMinutes', { method: 'POST', body: { targetUserId, minutes } });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to grant voice minutes' };
    }
  }

  async blockUser(targetUserId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiCall<any>('/blockUser', { method: 'POST', body: { targetUserId } });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to block player' };
    }
  }

  async unblockUser(targetUserId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiCall<any>('/unblockUser', { method: 'POST', body: { targetUserId } });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to unblock player' };
    }
  }

  async adminSendCoins(targetUserId: string, currency: 'playcoins' | 'pkcash' | 'bundle', amount: number, message?: string): Promise<{
    success: boolean;
    error?: string;
    newBalance?: number;
  }> {
    try {
      const data = await apiCall<any>('/admin/sendCoins', {
        method: 'POST',
        body: { targetUserId, currency, amount, message },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to send coins' };
    }
  }

  /**
   * Correct a wallet balance - the undo for adminSendCoins.
   * mode 'set'   -> amount becomes the exact new balance
   * mode 'delta' -> amount is added (pass a negative number to take away)
   */
  async adminAdjustBalance(
    targetUserId: string,
    currency: 'playcoins' | 'pkcash',
    mode: 'set' | 'delta',
    amount: number,
    reason?: string,
  ): Promise<{
    success: boolean; error?: string;
    previousBalance?: number; newBalance?: number; delta?: number;
  }> {
    try {
      const data = await apiCall<any>('/admin/adjustBalance', {
        method: 'POST',
        body: { targetUserId, currency, mode, amount, reason },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to adjust balance' };
    }
  }

  async createTestTournament(opts: {
    startInMinutes?: number;
    minPlayers?: number;
    currency?: 'playcoins' | 'pkcash';
    buyIn?: number;
    name?: string;
    structure?: 'regular' | 'turbo' | 'hyper' | 'deep';
    maxPlayersPerTable?: number;
  } = {}): Promise<{
    success: boolean;
    error?: string;
    tournamentId?: string;
    name?: string;
    startsAt?: string;
  }> {
    try {
      const data = await apiCall<any>('/admin/createTestTournament', {
        method: 'POST',
        body: opts,
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create test tournament' };
    }
  }

  async seedTournamentBots(tournamentId: string, count: number): Promise<{
    success: boolean;
    error?: string;
    added?: number;
    requested?: number;
    totalEntrants?: number;
    botIds?: string[];
  }> {
    try {
      const data = await apiCall<any>('/admin/seedTournamentBots', {
        method: 'POST',
        body: { tournamentId, count },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to seed bots' };
    }
  }

  async killTestTournament(tournamentId: string, refundRealPlayers = true): Promise<{
    success: boolean;
    error?: string;
    botsRemoved?: number;
    realPlayersRefunded?: number;
    tablesEnded?: number;
  }> {
    try {
      const data = await apiCall<any>('/admin/killTestTournament', {
        method: 'POST',
        body: { tournamentId, refundRealPlayers },
      });
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to kill tournament' };
    }
  }

  // ==========================================================================
  //  RABBIT HUNT - Reveal would-be runout after a hand ends early

  // ==========================================================================
  async dealRabbitHunt(tableId: string, handNumber: number): Promise<{
    success: boolean;
    error?: string;
    handNumber?: number;
    existingCommunityCards?: Array<{ rank: string; suit: string }>;
    rabbitFlop?: Array<{ rank: string; suit: string }>;
    rabbitTurn?: Array<{ rank: string; suit: string }>;
    rabbitRiver?: Array<{ rank: string; suit: string }>;
    fullBoard?: Array<{ rank: string; suit: string }>;
    cached?: boolean;
  }> {
    try {
      const data = await apiCall<any>('/dealRabbitHunt', {
        method: 'POST',
        body: { tableId, handNumber },
      });
      return { success: true, ...data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to deal rabbit hunt',
      };
    }
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const gameApiService = new GameApiService();
export default gameApiService;